import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { useNutritionPresets } from '../../hooks/useNutritionPresets'
import { NutritionPreset } from '../../types'
import { Label } from '../ui/label'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import { X, Search } from 'lucide-react'

interface PresetSelectorProps {
  userId: string
  selectedPreset: NutritionPreset | null
  onPresetSelect: (preset: NutritionPreset | null) => void
}

export function PresetSelector({ userId, selectedPreset, onPresetSelect }: PresetSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const { data: presets, isLoading } = useNutritionPresets(userId)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const filteredPresets = useMemo(() => {
    if (!presets) return []
    if (!searchTerm) return presets
    
    return presets.filter(preset => 
      preset.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [presets, searchTerm])

  const getPresetSummary = useCallback((preset: NutritionPreset) => {
    const parts = []
    if (preset.carbs_g) parts.push(`${preset.carbs_g}g carbs`)
    if (preset.fluids_ml) parts.push(`${preset.fluids_ml}ml fluids`)
    if (preset.sodium_mg) parts.push(`${preset.sodium_mg}mg sodium`)
    if (preset.caffeine_mg) parts.push(`${preset.caffeine_mg}mg caffeine`)
    return parts.join(', ')
  }, [])

  const handlePresetClick = useCallback((preset: NutritionPreset) => {
    onPresetSelect(preset)
    setShowDropdown(false)
    setSearchTerm('')
    setFocusedIndex(-1)
  }, [onPresetSelect])

  const handleClearPreset = useCallback(() => {
    onPresetSelect(null)
    setSearchTerm('')
    setFocusedIndex(-1)
  }, [onPresetSelect])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showDropdown || filteredPresets.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setFocusedIndex(prev => 
          prev < filteredPresets.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setFocusedIndex(prev => 
          prev > 0 ? prev - 1 : filteredPresets.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (focusedIndex >= 0 && focusedIndex < filteredPresets.length) {
          handlePresetClick(filteredPresets[focusedIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        setShowDropdown(false)
        setFocusedIndex(-1)
        break
    }
  }, [showDropdown, filteredPresets, focusedIndex, handlePresetClick])

  // Reset focus when filtered presets change
  useEffect(() => {
    if (focusedIndex >= filteredPresets.length) {
      setFocusedIndex(filteredPresets.length > 0 ? 0 : -1)
    }
  }, [filteredPresets.length, focusedIndex])

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
        setFocusedIndex(-1)
      }
    }

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDropdown])

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Label>Use Preset (optional)</Label>
        <div className="flex items-center justify-center p-2 border rounded-md bg-gray-50">
          <span className="text-sm text-gray-500">Loading presets...</span>
        </div>
      </div>
    )
  }

  if (!presets || presets.length === 0) {
    return null // Don't show selector if no presets exist
  }

  return (
    <div className="space-y-2">
      <Label>Use Preset (optional)</Label>
      <div className="relative">
        {selectedPreset ? (
          <div className="flex items-center justify-between p-2 border rounded-md bg-gray-50">
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">{selectedPreset.name}</p>
              <p className="text-xs text-gray-600">{getPresetSummary(selectedPreset)}</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClearPreset}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                ref={inputRef}
                type="text"
                placeholder="Search presets..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setShowDropdown(true)
                  setFocusedIndex(-1)
                }}
                onFocus={() => setShowDropdown(true)}
                onKeyDown={handleKeyDown}
                className="pl-9"
                role="combobox"
                aria-expanded={showDropdown}
                aria-haspopup="listbox"
                aria-autocomplete="list"
              />
            </div>

            {showDropdown && (
              <div 
                ref={dropdownRef}
                className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto"
                role="listbox"
              >
                {filteredPresets.length === 0 ? (
                  <div className="p-3 text-sm text-gray-500" role="option" aria-selected="false">
                    {searchTerm ? 'No presets match your search' : 'No presets available'}
                  </div>
                ) : (
                  <div className="py-1">
                    {filteredPresets.map((preset, index) => (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => handlePresetClick(preset)}
                        onMouseEnter={() => setFocusedIndex(index)}
                        className={`w-full px-3 py-2 text-left focus:outline-none ${
                          index === focusedIndex 
                            ? 'bg-blue-50 text-blue-900' 
                            : 'hover:bg-gray-50 focus:bg-gray-50'
                        }`}
                        role="option"
                        aria-selected={index === focusedIndex}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{preset.name}</p>
                            <p className="text-xs text-gray-600 mt-0.5">
                              {getPresetSummary(preset)}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}